import { useCallback, useEffect, useState } from "react";
import { History, Search } from "lucide-react";
import api from "../lib/api";
import { AuditList } from "../components/audit/AuditList";

const inputCls = "px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent rounded-sm";
const PAGE = 100;

export default function AuditLog() {
    const [meta, setMeta] = useState(null);
    const [f, setF] = useState({ section: "", user_id: "", action: "", start: "", end: "", q: "" });
    const [data, setData] = useState(null);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => { api.get("/audit/meta").then((r) => setMeta(r.data)).catch(() => setMeta({ sections: [], users: [], actions: {} })); }, []);

    const load = useCallback(async () => {
        setLoading(true);
        const p = new URLSearchParams({ limit: String(PAGE), skip: String(page * PAGE) });
        Object.entries(f).forEach(([k, v]) => v && p.set(k, v));
        try { const { data } = await api.get(`/audit?${p}`); setData(data); } catch { setData({ items: [], total: 0 }); }
        finally { setLoading(false); }
    }, [f, page]);
    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

    const set = (k, v) => { setPage(0); setF({ ...f, [k]: v }); };
    const pages = data ? Math.ceil(data.total / PAGE) : 0;

    return (
        <div className="px-4 md:px-10 py-6 md:py-10 max-w-7xl mx-auto animate-fade-in" data-testid="audit-page">
            <div className="mb-8">
                <div className="label-eyebrow mb-2">Nastavení firmy</div>
                <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter text-slate-900 inline-flex items-center gap-3"><History className="w-8 h-8" /> Historie změn</h1>
                <p className="text-sm text-slate-600 mt-2 max-w-2xl">Kdo, kdy a co změnil u faktur, zakázek, rozpočtů, hodin, výplat, knihy jízd, nastavení i týmu. Záznamy nelze upravit ani smazat a uchovávají se minimálně {meta?.retention_years || 10} let. Hesla a klíče se neukládají.</p>
            </div>

            <div className="bg-white border border-slate-200 p-4 mb-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="audit-filters">
                <select value={f.section} onChange={(e) => set("section", e.target.value)} data-testid="audit-filter-section" className={inputCls}>
                    <option value="">Všechny sekce</option>
                    {(meta?.sections || []).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <select value={f.user_id} onChange={(e) => set("user_id", e.target.value)} data-testid="audit-filter-user" className={inputCls}>
                    <option value="">Všichni uživatelé</option>
                    {(meta?.users || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
                <select value={f.action} onChange={(e) => set("action", e.target.value)} data-testid="audit-filter-action" className={inputCls}>
                    <option value="">Všechny akce</option>
                    {Object.entries(meta?.actions || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input type="date" value={f.start} onChange={(e) => set("start", e.target.value)} data-testid="audit-filter-start" className={inputCls} />
                <input type="date" value={f.end} onChange={(e) => set("end", e.target.value)} data-testid="audit-filter-end" className={inputCls} />
                <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" /><input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Hledat doklad, zakázku, jméno…" data-testid="audit-filter-q" className={inputCls + " pl-8 w-full"} /></div>
            </div>

            <div className="bg-white border border-slate-200">
                <div className="px-4 py-3 border-b-2 border-slate-900 flex items-center justify-between text-xs text-slate-600">
                    <span data-testid="audit-total">{data ? `${data.total} záznamů` : ""}</span>
                    {pages > 1 && <span className="inline-flex items-center gap-2">
                        <button disabled={page === 0} onClick={() => setPage(page - 1)} className="px-2 py-1 border border-slate-300 disabled:opacity-40 rounded-sm">‹</button>
                        {page + 1} / {pages}
                        <button disabled={page >= pages - 1} onClick={() => setPage(page + 1)} className="px-2 py-1 border border-slate-300 disabled:opacity-40 rounded-sm">›</button>
                    </span>}
                </div>
                <AuditList items={data?.items || []} loading={loading && !data} />
            </div>
        </div>
    );
}
